import { TileType } from '../types/map.types';

/** World-space width of one grid cell in pixels. */
export const tileWidth = 24;
/** World-space height of one grid cell in pixels. */
export const tileHeight = 24;

/**
 * Each tile type maps to one or more tileset layers (back-to-front).
 * A single string = one layer. An array = base layer + overlay(s).
 * Example: wall: ['tilled-dirt', 'hills'] → tilled-dirt drawn first, hills on top.
 */
export type TilesetLayer = string | string[];
export type TilesetMapping = Record<TileType, TilesetLayer>;

/** Helper: normalize a layer value to always be an array. */
export const normalizeLayers = (layer: TilesetLayer): string[] =>
  typeof layer === 'string' ? [layer] : layer;

// Per-biome blob tilesets (parsed from the PMD exports via
// tools/parse-pmd-tileset.py): one wall / ground / water 12x4 blob each, in
// public/tiles/<biome>-{wall,ground,water}.png.
const biomeTileset = (id: string): TilesetMapping => ({
  wall: `${id}-wall`,
  ground: `${id}-ground`,
  water: `${id}-water`,
});

/**
 * Tileset assignments per floor theme. Keys must match `ZoneSpec.tileset` /
 * `finalBoss.tileset` in dungeons.config.ts — one per Content Bible biome.
 */
export const floorTilesets: Record<string, TilesetMapping> = {
  'beta-clear-forest': biomeTileset('clear-forest'),
  'beta-damp-caves': biomeTileset('damp-caves'),
  'beta-old-ruins': biomeTileset('old-ruins'),
  'beta-lost-galleries': biomeTileset('lost-galleries'),
  'beta-thorn-maze': biomeTileset('thorn-maze'),
  'beta-windy-peak': biomeTileset('windy-peak'),
  'beta-deep-caverns': biomeTileset('deep-caverns'),
  'beta-summit-keep': biomeTileset('summit-keep'),
  'beta-still-ponds': biomeTileset('still-ponds'),
  'beta-calm-glade': biomeTileset('calm-glade'),
  'beta-endless-rift': biomeTileset('endless-rift'),
  'beta-endless-rift-deep': biomeTileset('endless-rift-deep'),
};

/** Fallback when a floor references an unmapped tileset (defense-in-depth). */
export const DEFAULT_TILESET_MAPPING: TilesetMapping =
  biomeTileset('clear-forest');
